// Avisos por correo de pago a repartidores (Configuración de empresa → "Avisos de pago a
// repartidores"): encender/apagar el aviso "documento tributario recibido" y elegir a qué
// usuarios del equipo les llega. Sin elección, va a todos los administradores activos. Las
// cuentas dadas de baja nunca reciben el aviso (lo filtra el backend al enviar), así que
// acá ni se listan.
//
// Autocontenida como RemitoRecipientsConfig: GET/PUT /billing/driver-payroll/
// notification-settings, scopeado por el JWT. El atajo "Configuración" del menú ⋯ de Pago
// a repartidores apunta a esta pestaña (?tab=driver-payment-notifications).
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, MailCheck, RefreshCw, Save } from 'lucide-react';
import { SectionCard } from '../../../../components/ui/SectionCard';
import { Switch } from '../../../../components/ui/Switch';
import { Button } from '../../../../components/ui/Button';
import { Skeleton } from '../../../../components/ui/Skeleton';
import {
  getDriverBillingNotificationSettings,
  updateDriverBillingNotificationSettings,
} from '../../../../api/billing';
import { showToast } from '../../../../utils/showToast';
import { roleRoutes } from '../../../../utils/roles';

// Roles que el backend admite como destinatarios (misma allowlist de staff). Los
// administradores son el destino por defecto cuando no se elige a nadie.
const ROLE_LABEL = {
  [roleRoutes.admin]: 'Administrador',
  [roleRoutes.superadmin]: 'Superadministrador',
  [roleRoutes.supervisor]: 'Supervisor',
  [roleRoutes.support]: 'Soporte',
};
const DEFAULT_ROLE_IDS = new Set([roleRoutes.admin, roleRoutes.superadmin]);

const snapshotOf = (enabled, selected) =>
  JSON.stringify({ enabled, selected: [...selected].sort() });

export default function DriverPaymentNotificationsSection() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [candidates, setCandidates] = useState([]);
  const [snapshot, setSnapshot] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    getDriverBillingNotificationSettings()
      .then((res) => {
        if (!active) return;
        const data = res?.data ?? {};
        const nextEnabled = data.documentUploadedEnabled !== false;
        const nextSelected = new Set(data.documentUploadedRecipients ?? []);
        setEnabled(nextEnabled);
        setSelected(nextSelected);
        setCandidates(data.candidates ?? []);
        setSnapshot(snapshotOf(nextEnabled, nextSelected));
      })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 403) setForbidden(true);
        else {
          setLoadError(true);
          showToast(t('No se pudo cargar la configuración'), false);
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // `t` fuera de las deps a propósito: cambiar de idioma no debe refetchear y
    // descartar lo marcado sin guardar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const dirty = snapshotOf(enabled, selected) !== snapshot;

  const defaultCount = useMemo(
    () => candidates.filter((c) => DEFAULT_ROLE_IDS.has(c.roleId)).length,
    [candidates],
  );

  const toggle = (userId) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  const handleReset = () => {
    const snap = JSON.parse(snapshot);
    setEnabled(snap.enabled);
    setSelected(new Set(snap.selected));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDriverBillingNotificationSettings({
        documentUploadedEnabled: enabled,
        documentUploadedRecipients: [...selected],
      });
      setSnapshot(snapshotOf(enabled, selected));
      showToast(t('Avisos por correo guardados'), true);
    } catch (err) {
      showToast(err?.response?.data?.message || t('No se pudo guardar la configuración'), false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <SectionCard
        icon={<AlertTriangle className="w-4 h-4" />}
        title={t('Sin configuración disponible')}
        subtitle={t('Tu usuario no tiene permiso para configurar los avisos de pago a repartidores.')}
      />
    );
  }

  if (loadError) {
    return (
      <SectionCard
        icon={<AlertTriangle className="w-4 h-4" />}
        title={t('No se pudo cargar la configuración')}
        subtitle={t('Reintenta en un momento.')}
        action={
          <Button
            variant="outline"
            onClick={() => setReloadKey((k) => k + 1)}
            iconLeft={<RefreshCw size={16} />}
          >
            {t('Reintentar')}
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        icon={<MailCheck className="w-4 h-4" />}
        title={t('Documento tributario recibido')}
        subtitle={t('Qué se avisa y a quién cuando un repartidor sube su documento tributario.')}
      >
        <div className="flex flex-col gap-5">
          <Switch
            checked={enabled}
            onChange={setEnabled}
            disabled={saving}
            label={t('Avisar por correo cuando un repartidor suba su documento tributario')}
          />

          <div className={enabled ? '' : 'opacity-50'}>
            <div className="text-sm font-medium text-fg">{t('Destinatarios')}</div>
            <p className="mt-1.5 text-xs text-fg-subtle">
              {selected.size === 0
                ? t('Sin selección, el aviso llega a todos los administradores activos ({{n}}).', { n: defaultCount })
                : t('El aviso llega solo a los {{n}} usuarios seleccionados.', { n: selected.size })}
            </p>
            {candidates.length === 0 ? (
              <p className="mt-3 text-sm text-fg-muted">{t('No hay usuarios del equipo activos.')}</p>
            ) : (
              <ul className="mt-3 flex flex-col divide-y divide-border rounded-md border border-border bg-bg">
                {candidates.map((c) => (
                  <li key={c.userId}>
                    <label className="flex cursor-pointer items-center gap-3 px-3 py-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={selected.has(c.userId)}
                        disabled={!enabled || saving}
                        onChange={() => toggle(c.userId)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-fg">{c.name || c.email}</span>
                        <span className="block truncate text-xs text-fg-muted">{c.email}</span>
                      </span>
                      <span className="shrink-0 text-xs text-fg-muted">
                        {t(ROLE_LABEL[c.roleId] ?? 'Equipo')}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-3 text-xs text-fg-subtle">
              {t('Las cuentas dadas de baja nunca reciben el aviso.')}
            </p>
          </div>
        </div>
      </SectionCard>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button variant="outline" onClick={handleReset} disabled={!dirty || saving}>
          {t('Descartar cambios')}
        </Button>
        <Button
          onClick={handleSave}
          loading={saving}
          disabled={!dirty || saving}
          iconLeft={!saving ? <Save size={16} /> : undefined}
        >
          {saving ? t('Guardando…') : t('Guardar cambios')}
        </Button>
      </div>
    </div>
  );
}
